import * as React from 'react';

import { classNames, useConfirm, useTranslation, useSearchFilters, Tag } from '@components';
import { IOption, ISecurity, IWork } from '@models';
import {
    messageTypes, IMessage, translateNotificationIcon, messageTitle, MessageType
} from '@models/notifications';
import { NotificationMessageDisplayName } from './NotificationMessageDisplayName';
import { NotificationMessageEffect } from './NotificationMessageEffect';
import { MessageIcon } from './MessageIcon';
import * as DateUtils from '@utils/date-utils';

import './NotificationsCenter.scss';
import { currentLocale } from '../../i18n';

type ResolveMessageF = (id: string) => Promise<IOption<boolean>>;

export interface IProps {
    className?: string;
    messages: IMessage[];
    resolveMessage: ResolveMessageF;
    security: ISecurity;
    work: IWork;
    workId: number;
}

function NotificationMessage({ message, resolveMessage, onSelect, checked, onCheck }: {
    message: IMessage, resolveMessage: ResolveMessageF, onSelect: Function,
    checked: boolean,
    onCheck: Function,
}) {
    const { t } = useTranslation();

    const confirm = useConfirm({
        message: t('notifications.message.close-confirm'),
        accept: (messageId: string) => resolveMessage(messageId),
    });

    let langRegExp = new RegExp(/(--\w{2}--)/)

    const detectLanguages: any = (input: string, acc = [], level = 1) => {
        const m = langRegExp.exec(input);

        if (m && input.length > 0) {
            const lang = m[0];
            const index = input.indexOf(lang) + lang.length;
            return detectLanguages(input.substring(index), [...acc, lang], level + 1);
        }
        else {
            return acc;
        }
    }

    const languages: string[] = detectLanguages(message.message);
    const localeIncluded = languages.map(d => d.includes(currentLocale()) ? currentLocale() : undefined).filter(d => d !== undefined)[0];

    const getNotificationByLanguage = (input: string, language: string | undefined = undefined) => {
        if (language) {
            const i = input.indexOf('--' + language + '--');
            const l = input.indexOf('--', i + 6);
            const start = i >= 0 ? i + 6 : 0;

            return input.substring(start, l >= 0 ? l : input.length);
        }
        else if (input.indexOf('--') >= 0) {
            return input.substring(0, input.indexOf('--'));
        }
        else {
            return input;
        }
    }
    message.message = getNotificationByLanguage(message.message, localeIncluded)

    return <div
        className={classNames('message', message.severity)}>
        {message.icon &&
            <i onClick={() => onSelect(message)} className={translateNotificationIcon(message.icon)} />}
        <MessageIcon message={message} />

        <span className='message-date'>{DateUtils.format(message.date, 'd-m-y h:i')}</span>

        <span onClick={() => onSelect(message)}>
            {messageTitle(message)}
        </span>

        <span onClick={() => onSelect(message)}>
            <NotificationMessageDisplayName message={message} />
        </span>

        <span className='e' />
        <input type="checkbox" checked={checked} onClick={_ => onCheck()} id={message.id} />
        <span
            className='close-handle'
            onClick={e => confirm(message.id, e)}
            title={t('notifications.message.close')}>
            <i className='pi pi-times' />
        </span>
    </div>
}

type IMessageWithSearch = IMessage & {
    jobSearch: string;
    contractorSearch: string;
    messageSearch: string;
}

const filterMessage = (message: IMessageWithSearch, filters: any) => {
    return (filters.messageType == undefined || message.notificationType == filters.messageType)
        && (filters.job == null || filters.job == '' || message.jobSearch.includes(filters.job.toLowerCase()))
        && (filters.contractor == null || filters.contractor == '' || message.contractorSearch.includes(filters.contractor.toLowerCase()))
        && (filters.message == null || filters.message == '' || message.messageSearch.includes(filters.message.toLowerCase()))
        && (message.message != null)
        && (message.message != '')
        && (message.notificationType == MessageType.RELOAD_NOTIFICATIONS);
}

const filterMessages = (messages: IMessageWithSearch[], filters: any) => {
    return messages.filter(m => filterMessage(m, filters));
}

const contractorProperties = ['ContractorName', 'ContractorCode'];
const jobProperties = ['JobCode', 'JobName', 'JobDescription'];

const parseMessageProps = (data: any, propNames: string[]) => {
    const props = propNames.map(p => data[p] ?? '').join('').toLowerCase();

    return props ?? '';
}

const parseMessages = (messages: IMessage[]) => {
    return messages.map(m => ({
        ...m,
        jobSearch: parseMessageProps(m.data ?? {}, jobProperties),
        contractorSearch: parseMessageProps(m.data ?? {}, contractorProperties),
        messageSearch: m.message?.toLowerCase(),
    }));
}

const excludeNotificationTypes = [
    MessageType.INVALID_ITA,
    MessageType.RESOURCE_AUTHORIZED,
    MessageType.RESOURCE_REJECTED,
    MessageType.REQUIREMENT_EXPIRED,
];

export function NotificationsCenter(props: IProps) {
    const { t } = useTranslation();

    const confirm = useConfirm({
        message: t('notifications.messages.close-confirm'),
        accept: () => doRemoveCheckedMessages(),
    });

    const originalMessages = React.useRef<IMessageWithSearch[]>(
        parseMessages(props.messages));

    const [messages, setMessages] = React.useState<IMessageWithSearch[]>(originalMessages.current);
    const [selectedMessage, setSelectedMessage] = React.useState<IMessage | undefined>();
    const [checkedMessages, setCheckedMessages] = React.useState<IMessage[]>([]);

    const filters = props.workId == 21 ? useSearchFilters({
        name: 'notifications/list',
        workId: props.workId,
        references: {
            messageType: messageTypes
                .filter(m => !excludeNotificationTypes.includes(m.id))
                .map(m => ({ ...m, name: t(m.name) })),
        },
        persist: true
    }) : useSearchFilters({
        name: 'notifications/list',
        workId: props.workId ?? 0,
        references: {
            messageType: messageTypes.map(m => ({ ...m, name: t(m.name) })),
        },
        persist: true
    });

    React.useEffect(() => {
        if (filters.hasFilters()) {
            if (props.workId != null) {
                setMessages(filterMessages(originalMessages.current, filters.filters)
                    .filter(om => om.data)
                    .filter(om => om.data.WorkId == props.workId)
                    .sort((a, b) =>
                        new Date(b.date!).getTime() - new Date(a.date!).getTime()))
            } else {
                setMessages(filterMessages(originalMessages.current, filters.filters)
                    .sort((a, b) =>
                        new Date(b.date!).getTime() - new Date(a.date!).getTime()))
            }
        }
        else {
            if (props.workId != null) {
                setMessages(originalMessages.current
                    .filter(om => om.data.WorkId == props.workId)
                    .sort((a, b) =>
                        new Date(b.date!).getTime() - new Date(a.date!).getTime()));
            } else {
                setMessages(originalMessages.current
                    .sort((a, b) =>
                        new Date(b.date!).getTime() - new Date(a.date!).getTime()));
            }

        }
    }, [filters.filters]);

    React.useEffect(() => {
        const original = parseMessages(props.messages);
        originalMessages.current = original;
        if (filters.hasFilters()) {
            setMessages(filterMessages(original, filters.filters));
        }
        else {
            setMessages(original
                .sort((a, b) =>
                    new Date(b.date!).getTime() - new Date(a.date!).getTime()));
        }
    }, [props.messages]);

    const doRemoveCheckedMessages = () => {
        for (const message of checkedMessages) {
            props.resolveMessage(message.id);
        }
    }

    const doCheckAllMessages = () => {
        if (checkedMessages.length == messages.length) {
            setCheckedMessages([]);
        }
        else {
            setCheckedMessages(messages);
        }
    }

    const messagesToRender = messages
        .filter(m => m.notificationType != MessageType.RELOAD_NOTIFICATIONS);

    return <div className={classNames(props.className, 'notifications-center')}>
        <div className='notifications-title'>
            {t('Notifications')}
            <Tag
                className='small sm mr-left'
                value={messagesToRender.length} />
            <img src="/img/works/notifications.png" alt="gstore" />
            <span className='e' />
            <div className='search-filters' style={{ padding: "10px" }}>
                <i onClick={doCheckAllMessages} className="fas fa-check-double" title={t('notifications.remove.all')} style={{ color: "#717171" }} />
            </div>
            <div className='search-filters' style={{ padding: "10px" }}>
                <i className='fas fa-trash' onClick={checkedMessages.length == 0 ? undefined : confirm} title={t('notifications.remove')} style={{ color: "#717171" }} />
            </div>
            {filters.renderAsButton()}
        </div>

        <div className='notifications'>
        {console.log('###workId', props.workId)}
            {selectedMessage &&
                <NotificationMessageEffect
                    message={selectedMessage}
                    requestCancel={() => setSelectedMessage(undefined)}
                    resolveMessage={props.resolveMessage}
                    security={props.security}
                    workId={props.workId}
                    work={props.work} />}
            {messagesToRender.map(m =>
                <NotificationMessage
                    key={m.id}
                    message={m}
                    resolveMessage={props.resolveMessage}
                    onSelect={setSelectedMessage}
                    checked={checkedMessages.includes(m)}
                    onCheck={() => setCheckedMessages(checkedMessages.includes(m) ? checkedMessages.filter(r => r != m) : [...checkedMessages, m])}
                />)}
        </div>
    </div>
}
